import { OpenInNew } from '@mui/icons-material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    IconButton,
    Snackbar,
    Stack,
    Typography,
} from '@mui/material';
import { blue, grey } from '@mui/material/colors';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import { DateTime } from 'luxon';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { createStripeLoginLink } from '../../../api/organizations.api';
import { formatCurrency, formatDateStringToLocale } from '../../../app.functions';
import CustomDateRangePicker from '../../../components/FormControl/CustomDateRangePicker';
import PartnershipEarningsAnalytics from '../components/PartnershipEarningsAnalytics';
import { NetworkingActionTypes } from '../state/networking.state';
import {
    useDispatchContext,
    useNetworkingOrganization,
    useNetworkingPayouts,
} from '../state/NetworkingContext';

const PartnershipPayoutsScene = () => {
    const payouts = useNetworkingPayouts();
    const organization = useNetworkingOrganization();
    const dispatch = useDispatchContext();
    const { id } = useParams<{ id: string }>();
    const { push } = useHistory();

    const monthGroups = React.useMemo(() => {
        const groups: { label: string; key: string; rows: any[] }[] = [];
        for (const row of payouts.data ?? []) {
            const dt = DateTime.fromISO(row.payout_date);
            const key = dt.toFormat('yyyy-MM');
            const label = dt.toFormat('MMMM yyyy');
            const last = groups[groups.length - 1];
            if (last && last.key === key) {
                last.rows.push(row);
            } else {
                groups.push({ label, key, rows: [row] });
            }
        }
        return groups;
    }, [payouts.data]);

    const columns = React.useMemo(() => {
        const totalRows = monthGroups.reduce((sum, g) => sum + g.rows.length, 0);
        const target = Math.ceil(totalRows / 3);
        const cols: (typeof monthGroups)[] = [[], [], []];
        const colCounts = [0, 0, 0];
        let colIdx = 0;

        for (const group of monthGroups) {
            if (colIdx < 2 && colCounts[colIdx] >= target && colCounts[colIdx] > 0) {
                colIdx++;
            }
            cols[colIdx].push(group);
            colCounts[colIdx] += group.rows.length;
        }

        return cols;
    }, [monthGroups]);

    return (
        <Container
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
            maxWidth={'xl'}
        >
            <Stack
                direction={'column'}
                spacing={4}
                sx={{
                    py: 4,
                }}
            >
                <PartnershipEarningsAnalytics />
                <h2>Partnership Payouts List</h2>
                <Stack direction={'row'} spacing={2}>
                    <CustomDateRangePicker
                        onChange={(e: DateRange<DateTime>) => {
                            if (e[0] && e[1]) {
                                dispatch({
                                    type: NetworkingActionTypes.PAYOUTS_FILTERS_CHANGED,
                                    payload: {
                                        page: 1,
                                        'filter[paid_after]': e[0].toFormat('yyyy-MM-dd'),
                                        'filter[paid_before]': e[1].toFormat('yyyy-MM-dd'),
                                    },
                                });
                            }
                        }}
                        value={[
                            payouts.filters['filter[paid_after]']
                                ? DateTime.fromFormat(
                                      payouts.filters['filter[paid_after]'],
                                      'yyyy-MM-dd',
                                  )
                                : null,
                            payouts.filters['filter[paid_before]']
                                ? DateTime.fromFormat(
                                      payouts.filters['filter[paid_before]'],
                                      'yyyy-MM-dd',
                                  )
                                : null,
                        ]}
                    />
                    <Button
                        variant="outlined"
                        color="primary"
                        endIcon={<OpenInNew />}
                        onClick={async () => {
                            try {
                                const response = await createStripeLoginLink(
                                    organization.data?.uuid ?? '',
                                );
                                window.location.href = response.data.url;
                            } catch (e) {
                                console.error(e);
                            }
                        }}
                    >
                        View Bank And Transfer Details
                    </Button>
                </Stack>
            </Stack>

            <Box sx={{ width: '100%', pb: 2 }}>
                {payouts.loading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                        <CircularProgress />
                    </Stack>
                ) : !payouts.data?.length ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            No Payouts Yet
                        </Typography>
                    </Stack>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            columnGap: 6,
                        }}
                    >
                        {columns.map((col, colIdx) => (
                            <Box
                                key={colIdx}
                                sx={{
                                    border: `1px solid ${grey[300]}`,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        px: 1,
                                        py: 0.75,
                                        fontWeight: 700,
                                        fontSize: '0.8rem',
                                        borderBottom: `1px solid ${grey[300]}`,
                                        backgroundColor: grey[100],
                                    }}
                                >
                                    <span>Payout Date</span>
                                    <span>Amount</span>
                                </Box>
                                {col.map((group, groupIdx) => (
                                    <Box
                                        key={group.key}
                                        sx={{
                                            m: 0.5,
                                            mt: groupIdx === 0 ? 0.5 : 0,
                                            border: `1px solid ${grey[200]}`,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                backgroundColor: grey[50],
                                                borderBottom: `1px solid ${grey[200]}`,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {group.label}
                                        </Box>
                                        {group.rows.map((row: any, rowIdx: number) => (
                                            <Box
                                                key={row.uuid}
                                                onClick={() =>
                                                    push(`/networking/${id}/payouts/${row.uuid}`)
                                                }
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    px: 1,
                                                    py: 0.5,
                                                    fontSize: '0.85rem',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: blue[50],
                                                    },
                                                    borderBottom:
                                                        rowIdx < group.rows.length - 1
                                                            ? `1px solid ${grey[100]}`
                                                            : 'none',
                                                }}
                                            >
                                                <span style={{ flex: 1 }}>
                                                    {formatDateStringToLocale(row.payout_date)}
                                                </span>
                                                <span
                                                    style={{
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {formatCurrency({
                                                        amount: ((row.amount ?? 0) as number) / 100,
                                                        currency: 'USD',
                                                    })}
                                                </span>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Pagination */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end', mt: 1 }}>
                    <IconButton
                        disabled={!payouts.pagination.has_prev}
                        onClick={() => {
                            dispatch({
                                type: NetworkingActionTypes.PAYOUTS_FILTERS_CHANGED,
                                payload: {
                                    page: payouts.pagination.current_page - 1,
                                },
                            });
                        }}
                    >
                        <ArrowBackIosIcon />
                    </IconButton>
                    Page {payouts.pagination.current_page}
                    <IconButton
                        disabled={!payouts.pagination.has_next}
                        onClick={() => {
                            dispatch({
                                type: NetworkingActionTypes.PAYOUTS_FILTERS_CHANGED,
                                payload: {
                                    page: payouts.pagination.current_page + 1,
                                },
                            });
                        }}
                    >
                        <ArrowForwardIosIcon />
                    </IconButton>
                </Box>
            </Box>

            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={Boolean(payouts.errorMessage)}
                message={payouts.errorMessage}
                onClose={() => {
                    dispatch({
                        type: NetworkingActionTypes.PAYOUTS_ERROR_MESSAGE_CLEARED,
                    });
                }}
                autoHideDuration={3000}
            />
        </Container>
    );
};
export default PartnershipPayoutsScene;
